import { TZDate } from "@date-fns/tz";
import * as WindowManagement from "@omni/services/windowManagement";
import { WindowKeys } from "@omni/services/windowManagement";
import { CloudSpecialOpeningDay } from "@omni/types/api/store/cloud/shared/specialOpeningDay.model";
import { CloudWeekdayOpenings } from "@omni/types/api/store/cloud/shared/weekDayOpenings.model";
import { CloudStore } from "@omni/types/api/store/cloud/store.model";
import { Translate } from "@omni/types/utils/translate";
import { isDefined } from "@omni/utils/helper.utils";

import { formatDate } from "@/utils/date";
import { padStart } from "@/utils/textUtils";

import {
    add,
    isSameDay,
    isStoreClosedToday,
    numberToTime,
    TIMEZONE,
    timeToNumber,
    WEEKDAYS
} from "./storeInfo.helpers";

type MappedWeekDayOpenings = Record<string, { times: Array<number> }>;
type MappendSpecialOpeningDays = Array<{
    isClosed: boolean;
    date: TZDate;
    times: Array<number>;
}>;

type SelectedDay = { times: Array<number>; date: TZDate };

export type TStoreStatus = {
    isOpen: boolean;
    text: string;
    text2: string;
};

const prepWeekDayOpenings = (weekDayOpenings: CloudWeekdayOpenings) => {
    return Object.entries(weekDayOpenings).reduce((acc, [day, openings]) => {
        const openingTimes = [
            timeToNumber(openings.openingTime),
            timeToNumber(openings.middayClosingTime),
            timeToNumber(openings.middayOpeningTime),
            timeToNumber(openings.closingTime)
        ]
            .filter(isDefined)
            .sort((a, b) => a - b);

        if (openingTimes.length > 0) {
            acc[day] = { times: openingTimes };
        }
        return acc;
    }, {} as MappedWeekDayOpenings);
};

const prepSpecialDayOpenings = (
    specialOpeningDays: Array<CloudSpecialOpeningDay>
): MappendSpecialOpeningDays => {
    return specialOpeningDays
        .map((day) => ({
            isClosed: day.closed,
            date: new TZDate(day.date, TIMEZONE),
            times: [
                timeToNumber(day.openingTime),
                timeToNumber(day.closingTime)
            ]
                .filter(isDefined)
                .sort((a, b) => a - b)
        }))
        .sort((a, b) => {
            return a.date.getTime() - b.date.getTime();
        });
};

const getOpenDay = (
    weekDayOpenings: MappedWeekDayOpenings,
    specialOpeningDays: MappendSpecialOpeningDays,
    currentDate: TZDate,
    currentTime: number = 0
): { times: Array<number>; date: TZDate } | null => {
    const specialOpeningDay = specialOpeningDays.find((specialDay) => {
        return (
            isSameDay(specialDay.date, currentDate) &&
            !specialDay.isClosed &&
            currentTime <= specialDay.times[specialDay.times.length - 1]
        );
    });

    if (specialOpeningDay) {
        return {
            times: specialOpeningDay.times,
            date: specialOpeningDay.date
        };
    }

    const day = Number(formatDate(currentDate, "i"));
    const weekDayOpening = WEEKDAYS.at(day - 1)!;
    const weekDayOpeningInfo = weekDayOpenings[weekDayOpening];

    if (!weekDayOpeningInfo) {
        return getOpenDay(
            weekDayOpenings,
            specialOpeningDays,
            add(currentDate, { days: 1 })
        );
    }

    const isAfterClosingTimeOnFoundDay =
        currentTime >
        weekDayOpeningInfo.times[weekDayOpeningInfo.times.length - 1];

    if (isAfterClosingTimeOnFoundDay) {
        return getOpenDay(
            weekDayOpenings,
            specialOpeningDays,
            add(currentDate, { days: 1 })
        );
    }

    const currentlySpecialOpeningDay = specialOpeningDays.find((specialDay) => {
        return isSameDay(specialDay.date, currentDate);
    });

    if (currentlySpecialOpeningDay) {
        if (
            currentlySpecialOpeningDay.isClosed ||
            currentTime >=
                currentlySpecialOpeningDay.times[
                    currentlySpecialOpeningDay.times.length - 1
                ]
        ) {
            return getOpenDay(
                weekDayOpenings,
                specialOpeningDays,
                add(currentDate, { days: 1 })
            );
        }

        return {
            times: currentlySpecialOpeningDay.times,
            date: currentlySpecialOpeningDay.date
        };
    }

    return {
        times: weekDayOpeningInfo.times,
        date: new TZDate(currentDate.getTime(), TIMEZONE)
    };
};

const getSelectedDayInfo = (
    selectedDay: {
        times: Array<number>;
        date: TZDate;
    },
    currentTime: number,
    currentDate: TZDate
) => {
    let nextFoundTimeIndex = selectedDay.times.findIndex((time) => {
        return time >= currentTime;
    });

    if (!isSameDay(selectedDay.date, currentDate)) {
        nextFoundTimeIndex = 0;
    }

    const isOpen = nextFoundTimeIndex % 2 !== 0;
    const nextFoundTime = numberToTime(selectedDay.times[nextFoundTimeIndex]);
    return { isOpen, nextFoundTime };
};

const getMockedDate = () => {
    const globalMockDate = WindowManagement.getObject(
        WindowKeys.MOCK_DATE
    )?.getMockDate();
    if (!globalMockDate) return null;

    return new TZDate(globalMockDate, TIMEZONE);
};

export const getStoreInfo = (
    storeDetail: CloudStore,
    t: Translate
): TStoreStatus => {
    const currentDate = getMockedDate() || TZDate.tz(TIMEZONE);

    /// Is store closed today based on openSince and openUntil
    if (
        isStoreClosedToday(
            storeDetail.openSince,
            storeDetail.openUntil,
            currentDate
        )
    ) {
        return {
            isOpen: false,
            text: t("header.navigation.shopfinder.store.closed"),
            text2: t("header.navigation.shopfinder.store.closed")
        };
    }

    const weekDayOpenings = prepWeekDayOpenings(storeDetail.weekDayOpenings);
    const specialOpeningDays = prepSpecialDayOpenings(
        storeDetail.specialOpeningDays
    );

    const hasWeekDayOpenings = Object.keys(weekDayOpenings).length > 0;
    const hasSpecialOpeningDays = specialOpeningDays.length > 0;

    let selectedDay: SelectedDay | null = null;
    const currentTime = timeToNumber(
        `${currentDate.getHours()}:${padStart(currentDate.getMinutes().toString(), 2, "0")}`
    )!;

    /// check if there are only special opening days
    if (!hasWeekDayOpenings && hasSpecialOpeningDays) {
        const firstOpenSpecialDay = specialOpeningDays.find(
            (day) =>
                !day.isClosed && currentTime <= day.times[day.times.length - 1]
        );

        if (firstOpenSpecialDay) {
            selectedDay = {
                times: firstOpenSpecialDay.times,
                date: firstOpenSpecialDay.date
            };
        }
    }

    if (!selectedDay && hasWeekDayOpenings) {
        selectedDay = getOpenDay(
            weekDayOpenings,
            specialOpeningDays,
            currentDate,
            currentTime
        );
    }

    if (selectedDay) {
        const { isOpen, nextFoundTime } = getSelectedDayInfo(
            selectedDay,
            currentTime,
            currentDate
        );

        const hours = Number(nextFoundTime.split(":")[0]);
        const minutes = Number(nextFoundTime.split(":")[1]);

        const timeZonedNextFoundTime = selectedDay.date.setHours(
            hours,
            minutes,
            0,
            0
        );

        const displayDate = new TZDate(timeZonedNextFoundTime, TIMEZONE);
        const nextFoundTimeDisplay = formatDate(displayDate, "HH:mm");

        return {
            isOpen,
            text: isOpen
                ? t(
                      "header.navigation.shopfinder.store.closesAt",
                      nextFoundTimeDisplay
                  )
                : t(
                      "header.navigation.shopfinder.store.opensAt",
                      nextFoundTimeDisplay
                  ),
            text2: isOpen
                ? t(
                      "form.searchstore.storeCard.status.closesAt",
                      nextFoundTimeDisplay
                  )
                : t(
                      "form.searchstore.storeCard.status.opensAt",
                      formatDate(displayDate, "dd/MM"),
                      nextFoundTimeDisplay
                  )
        };
    }

    return {
        isOpen: false,
        text: t("header.navigation.shopfinder.store.closed"),
        text2: t("header.navigation.shopfinder.store.closed")
    };
};
